import { HttpClient } from '@adapters/HttpClient';
import {
  EligibleStorage,
  EligibleStorageFilters,
  EligibleStoragePage,
  BlockedStoragesPage,
} from '../models/EligibleStorage';

interface GetEligibleStoragesResponse {
  data: EligibleStorage[];
  meta: {
    total: number;
    perPage: number;
    currentPage: number;
    lastPage: number;
  };
  links: {
    first: string;
    prev: string | null;
    next: string | null;
    last: string;
  };
}

interface RemoveFromPreinvoiceResponse {
  status: string;
  id: number;
  preinvoice_id: null;
}

interface CreateStoragePreInvoiceRequest {
  storage_ids: number[];
  type?: 'STORAGE';
}

interface CreateStoragePreInvoiceResponse {
  id: number;
  status: string;
  type: string;
  total: number;
  created_at: string;
}

function buildAdvancedFiltersParams(searchFilter: {
  filters: any[];
  logic: string;
}) {
  const params: Record<string, any> = {};

  if (searchFilter.filters && Array.isArray(searchFilter.filters)) {
    searchFilter.filters.forEach((filter, index) => {
      params[`advanced_filters[filters][${index}][column]`] = filter.column;
      params[`advanced_filters[filters][${index}][operator]`] =
        filter.operator ?? filter.action;

      if (
        filter.value !== undefined &&
        filter.value !== null &&
        !(Array.isArray(filter.value) && filter.value.length === 0)
      ) {
        params[`advanced_filters[filters][${index}][value]`] = filter.value;
      }
    });
  }

  if (searchFilter.logic) {
    params['advanced_filters[logic]'] = searchFilter.logic;
  }

  return params;
}

export class EligibleStorageApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getEligibleStorages(
    filters: EligibleStorageFilters = {}
  ): Promise<EligibleStoragePage> {
    const {
      searchFilter,
      search,
      date_from,
      date_to,
      product_id,
      sortBy,
      sortDirection,
      page = 1,
      limit = 25,
    } = filters;

    const params: Record<string, any> = {
      page,
      per_page: limit,
      ...(search ? { search } : {}),
      ...(date_from ? { date_from } : {}),
      ...(date_to ? { date_to } : {}),
      ...(product_id ? { product_id } : {}),
      ...(sortBy ? { 'sorting[column]': sortBy } : {}),
      ...(sortDirection ? { 'sorting[direction]': sortDirection } : {}),
      ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
    };

    const response = await this.httpClient.get<GetEligibleStoragesResponse>(
      '/accounting/storages/eligible',
      params,
      {},
      page === 1
    );

    return {
      storages: response.data,
      nextPage:
        response.meta.currentPage < response.meta.lastPage
          ? response.meta.currentPage + 1
          : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  async removeFromPreinvoice(
    id: number,
    reason?: string
  ): Promise<RemoveFromPreinvoiceResponse> {
    const response = await this.httpClient.post<RemoveFromPreinvoiceResponse>(
      `/accounting/storages/${id}/remove-from-preinvoice`,
      reason ? { reason } : {}
    );
    return response;
  }

  async createStoragePreInvoice(
    storageIds: number[]
  ): Promise<CreateStoragePreInvoiceResponse> {
    const response =
      await this.httpClient.post<CreateStoragePreInvoiceResponse>(
        '/invoices/pre-invoices/storages',
        { storage_ids: storageIds }
      );
    return response;
  }

  /**
   * Prefactura storages usando filtros avanzados
   * @param advancedFilters Filtros avanzados con la estructura { filters: [], logic: 'AND'|'OR' }
   * @returns Objeto de pre-factura creada
   */
  async bulkInvoiceStoragesByFilters(advancedFilters: { filters: any[]; logic: string }): Promise<any> {
    const response = await this.httpClient.post<any>(
      `/invoices/pre-invoices/storages`,
      { advanced_filters: advancedFilters }
    );
    return response;
  }

  /**
   * Bloquea un storage
   * POST /api/v1/invoices/storages/{id}/block
   * @param id Storage ID
   * @param payload Datos requeridos por la API: storage_block_reason_id (number|string), comment (string)
   */
  async blockStorage(
    id: number,
    payload?: { storage_block_reason_id?: number | string; comment?: string }
  ): Promise<any> {
    const response = await this.httpClient.post<any>(
      `/invoices/storages/${id}/block`,
      payload || {}
    );
    return response;
  }

  /**
   * Desbloquea un storage
   * DELETE /api/v1/invoices/storages/{id}/block
   * @param id ID del registro de bloqueo (accounting_storage_blocks.id), no el storage_id
   * @returns Storage desbloqueado con financial_status = NOT_INVOICED
   */
  async unblockStorage(id: number): Promise<any> {
    const response = await this.httpClient.delete<any>(
      `/invoices/storages/${id}/block`
    );
    return response;
  }

  /**
   * Obtiene los storages bloqueados de forma paginada
   * GET /api/v1/invoices/storages/block
   * Incluye razón, comentario y usuario que lo bloqueó
   */
  async getBlockedStoragesPaginated(
    filters: EligibleStorageFilters = {}
  ): Promise<BlockedStoragesPage> {
    const {
      searchFilter,
      search,
      sortBy = 'id',
      sortDirection = 'desc',
      page = 1,
      limit = 25,
    } = filters;

    const params: any = {
      ...(sortBy && { 'sorting[column]': sortBy }),
      ...(sortDirection && { 'sorting[direction]': sortDirection }),
      page,
      per_page: limit,
      ...(search && { search }),
      ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
    };

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>(
      '/invoices/storages/block',
      params,
      {},
      showGlobalLoading
    );

    return {
      blockedStorages: response.data,
      nextPage:
        response.meta.currentPage < response.meta.lastPage
          ? response.meta.currentPage + 1
          : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }
}
